export interface DockPageMeta {
	id: string;
	path: string;
	title: string;
	routePattern?: string;
	lazyMountUntilActive?: boolean;
	hideRibbon?: boolean;
	keepOwnLink?: boolean;
}

export interface ResolvedDockPage {
	id: string;
	componentId: string;
	path: string;
	title: string;
}

export interface DockDetailRoute {
	componentId: string;
	match: (pathname: string) => { id: string; path: string; title: string } | null;
}

export interface DockPageRegistryOptions {
	excludeNamespaceRootFallback?: boolean;
}

export interface DockPageRegistry<TPage extends DockPageMeta = DockPageMeta> {
	pagesById: Record<string, TPage>;
	defaultPage: TPage;
	resolveDockPage: (pathname: string) => ResolvedDockPage | undefined;
}

const isNamespaceRootPath = (page: DockPageMeta, pages: DockPageMeta[]): boolean =>
	pages.length > 1 && pages.every((other) => other === page || other.path.startsWith(`${page.path}/`));

const collectNamespaceRootPaths = (pages: DockPageMeta[]): Set<string> =>
	new Set(pages.filter((page) => isNamespaceRootPath(page, pages)).map((page) => page.path));

const createDockPageRegistry = <TPage extends DockPageMeta>(
	pages: TPage[],
	defaultPageId: string,
	detailRoutes: DockDetailRoute[] = [],
	options: DockPageRegistryOptions = {}
): DockPageRegistry<TPage> => {
	const pagesById = Object.fromEntries(pages.map((page) => [page.id, page]));
	const pagesByPathLengthDesc = [...pages].sort((a, b) => b.path.length - a.path.length);
	const defaultPage = pagesById[defaultPageId] ?? pages[0];
	const namespaceRootPaths = options.excludeNamespaceRootFallback
		? collectNamespaceRootPaths(pages)
		: new Set<string>();

	return {
		defaultPage,
		pagesById,
		resolveDockPage: (pathname) => {
			for (const route of detailRoutes) {
				const match = route.match(pathname);
				if (match) {
					return {
						componentId: route.componentId,
						id: match.id,
						path: match.path,
						title: match.title
					};
				}
			}
			const staticPage = pagesByPathLengthDesc.find(
				(page) => pathname === page.path || (!namespaceRootPaths.has(page.path) && pathname.startsWith(`${page.path}/`))
			);
			return staticPage
				? {
						componentId: staticPage.id,
						id: staticPage.id,
						path: staticPage.path,
						title: staticPage.title
					}
				: undefined;
		}
	};
};

export default createDockPageRegistry;
